import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarClock, Check, Copy, CreditCard, Gift, Loader2 } from 'lucide-react';
import { fetchRenouvellement, ouvrirRenouvellement } from '../services/api';

type Option = {
  code: string;
  nom: string;
  description: string;
  prix: number;
  detenue: boolean;
};

type Renouvellement = {
  association: string;
  periodicite: 'mensuel' | 'annuel';
  expireLe: string | null;
  joursRestants: number | null;
  socle: { code: string; nom: string; prix: number } | null;
  options: Option[];
  total: number;
  codeParrain: string | null;
  parrainageActif: boolean;
  parrainagesCredites: number;
};

const euros = (montant: number) =>
  montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' €';

/** « 14 septembre 2026 » — une date qu'on lit sans la déchiffrer. */
const jour = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/**
 * Ce qui sera reconduit à la prochaine échéance, et à quel prix.
 *
 * Un abonnement qui se reconduit tout seul finit par contenir des modules
 * dont personne ne se sert et que personne n'a décidé de garder. Reconduire
 * en silence puis s'entendre reprocher la facture est le meilleur moyen de
 * perdre un client : le bureau doit voir le détail avant que la somme passe,
 * et pouvoir retirer ce qui ne sert plus.
 *
 * Le bloc ne paraît qu'à l'approche de l'échéance. Le reste de l'année, il
 * n'aurait rien à dire — et un écran qui parle sans avoir rien à dire finit
 * par ne plus être lu.
 */
export default function RenouvellementAbonnement() {
  const [gardees, setGardees] = useState<string[] | null>(null);
  const [copie, setCopie] = useState(false);

  const retour = useMemo(
    () => new URLSearchParams(window.location.search).get('renouvellement'),
    []
  );

  /**
   * Le code de remise apporté par le lien du courriel.
   *
   * C'est celui de l'offre faite à une association qui a résilié : trente
   * pour cent sur la première année, et son espace tel qu'elle l'a laissé.
   * Il n'est pas vérifié ici — la boutique tranche, comme pour le total. Un
   * code accepté à l'écran et refusé au paiement ne serait qu'une déception
   * de plus.
   */
  const coupon = useMemo(
    () => new URLSearchParams(window.location.search).get('coupon')?.toUpperCase() || null,
    []
  );

  const query = useQuery({
    queryKey: ['admin-renouvellement'],
    queryFn: fetchRenouvellement,
    retry: false,
  });

  const donnees: Renouvellement | null = query.data?.renouvellement ?? null;

  // La sélection part de ce que l'association détient : reconduire à
  // l'identique doit être le geste par défaut, retirer un module une décision.
  useEffect(() => {
    if (donnees && gardees === null) {
      setGardees(donnees.options.filter((o) => o.detenue).map((o) => o.code));
    }
  }, [donnees, gardees]);

  const regler = useMutation({
    mutationFn: () => ouvrirRenouvellement(gardees ?? [], coupon ?? undefined),
    onSuccess: (d: any) => {
      if (d?.url) window.location.href = d.url;
    },
  });

  if (query.isPending || donnees === null) {
    return null;
  }

  // Trente jours : de quoi décider sans se sentir pressé, et assez près pour
  // que la question se pose vraiment.
  const proche = donnees.joursRestants !== null && donnees.joursRestants <= 30;
  const montreParrainage = donnees.parrainageActif && !!donnees.codeParrain;

  // Le renouvellement ne se montre qu'à l'approche de l'échéance — le reste
  // de l'année il n'aurait rien à dire. Le parrainage, lui, se donne quand
  // l'occasion se présente, c'est-à-dire n'importe quand : le cacher onze
  // mois sur douze revient à ne pas l'avoir.
  if (!proche && retour === null && !montreParrainage) {
    return null;
  }

  const choix = gardees ?? [];
  const total =
    (donnees.socle?.prix ?? 0) +
    donnees.options.filter((o) => choix.includes(o.code)).reduce((s, o) => s + o.prix, 0);

  const suffixe = donnees.periodicite === 'annuel' ? '/an' : '/mois';

  return (
    <div className="space-y-4">
      {retour === 'merci' && (
        <div className="rounded-xl border border-green-200 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200 px-4 py-3 text-sm flex items-start gap-2">
          <Check className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Merci ! Votre abonnement est reconduit dès que le paiement est confirmé, et les modules
            que vous avez retirés s'éteindront à cette date.
          </p>
        </div>
      )}
      {retour === 'annule' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 px-4 py-3 text-sm">
          Renouvellement interrompu : rien n'a été débité, votre abonnement est inchangé.
        </div>
      )}

      {/* Le retour après une résiliation : on ne fait pas semblant que rien
          ne s'est passé, et on ne s'excuse pas non plus. On dit ce qui se
          passe si le règlement va au bout. */}
      {retour === 'retour' && coupon && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-200 px-4 py-3 text-sm">
          <p className="font-semibold">Bon retour parmi nous.</p>
          <p className="mt-0.5">
            La remise de 30 % est appliquée au moment du paiement. Dès qu'il est confirmé, la saisie
            rouvre et vous retrouvez votre espace tel que vous l'avez laissé — rien n'a été touché.
          </p>
        </div>
      )}

      {/* Le code de parrainage. Il existait dans la boutique et nulle part
          ailleurs : l'association ne le connaissait que si on le lui avait
          écrit un jour, ce qui revient à ne pas avoir de parrainage. */}
      {montreParrainage && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 p-4 sm:p-5">
          <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <Gift className="w-4 h-4 text-amber-600" />
            Votre code de parrainage
          </p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Donnez-le à une association voisine. Dès qu'elle règle sa première échéance, un mois
            vous est offert : votre échéance recule d'un mois, ou votre prochaine mensualité est à
            zéro.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              title="Copier le code"
              onClick={() => {
                navigator.clipboard?.writeText(donnees.codeParrain!).then(
                  () => setCopie(true),
                  () => setCopie(false)
                );
                window.setTimeout(() => setCopie(false), 2000);
              }}
              className="font-mono text-base font-bold tracking-wider rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 px-4 py-2 hover:border-amber-500 inline-flex items-center gap-2"
            >
              {donnees.codeParrain}
              {copie ? (
                <Check className="w-4 h-4 text-emerald-600" />
              ) : (
                <Copy className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {copie && <span className="text-xs text-emerald-700 dark:text-emerald-400">Copié</span>}
          </div>

          {(donnees.parrainagesCredites ?? 0) > 0 && (
            <p className="mt-2 text-xs text-emerald-800 dark:text-emerald-300">
              {donnees.parrainagesCredites} mois déjà offert
              {donnees.parrainagesCredites! > 1 ? 's' : ''} grâce à vos parrainages.
            </p>
          )}
        </div>
      )}

      {/* Le renouvellement ne paraît qu'à l'approche de l'échéance : hors de
          cette fenêtre, il n'aurait rien à dire, et un écran qui parle sans
          avoir rien à dire finit par ne plus être lu. */}
      {(proche || retour !== null) && (
      <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 p-4 sm:p-5">
        <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <CalendarClock className="w-4 h-4 text-primary-600" />
          Votre abonnement se renouvelle
          {donnees.expireLe && <> le {jour(donnees.expireLe)}</>}
        </p>
        {donnees.joursRestants !== null && (
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
            {donnees.joursRestants > 0
              ? `Dans ${donnees.joursRestants} jour${donnees.joursRestants > 1 ? 's' : ''} — vous pouvez retirer ou ajouter des modules avant de régler.`
              : 'Échéance dépassée : réglez pour continuer sans interruption.'}
          </p>
        )}

        <ul className="mt-4 space-y-1.5">
          {donnees.socle && (
            <li className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{donnees.socle.nom}</span>
              <span className="tabular-nums font-medium text-gray-900 dark:text-gray-100">
                {euros(donnees.socle.prix)}
              </span>
            </li>
          )}
        </ul>

        {donnees.options.length > 0 && (
          <div className="mt-4 pt-4 border-t border-primary-200/60 dark:border-primary-800/60 space-y-2">
            {donnees.options.map((option) => {
              const garde = choix.includes(option.code);

              return (
                <label
                  key={option.code}
                  className="flex items-start gap-2.5 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={garde}
                    onChange={() =>
                      setGardees(
                        garde
                          ? choix.filter((c) => c !== option.code)
                          : [...choix, option.code]
                      )
                    }
                    className="mt-1 rounded border-gray-300 text-primary-600"
                  />
                  <span className="flex-1 flex items-baseline justify-between gap-3">
                    <span className="text-gray-700 dark:text-gray-300">
                      {option.nom}
                      {option.detenue && !garde && (
                        <span className="ml-1.5 text-xs text-amber-700 dark:text-amber-400">
                          — sera désactivé
                        </span>
                      )}
                      {!option.detenue && garde && (
                        <span className="ml-1.5 text-xs text-primary-700 dark:text-primary-400">
                          — ajouté
                        </span>
                      )}
                    </span>
                    <span
                      className={`tabular-nums font-medium ${
                        garde
                          ? 'text-gray-900 dark:text-gray-100'
                          : 'text-gray-400 dark:text-gray-500 line-through'
                      }`}
                    >
                      {euros(option.prix)}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-primary-200/60 dark:border-primary-800/60 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Total à régler{' '}
            <span className="text-lg tabular-nums">{euros(total)}</span>
            <span className="text-xs font-normal text-gray-500">{suffixe}</span>
            {/* Le montant remisé n'est pas calculé ici : c'est la boutique
                qui tranche, et afficher un chiffre qu'elle pourrait corriger
                reviendrait à annoncer un prix qu'on ne tiendra peut-être
                pas. On annonce la remise, pas le résultat. */}
            {coupon && (
              <span className="ml-2 text-xs font-normal text-emerald-700 dark:text-emerald-400">
                — 30 % déduits au paiement (code {coupon})
              </span>
            )}
          </span>
          <button
            onClick={() => regler.mutate()}
            disabled={regler.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
          >
            {regler.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            Renouveler
          </button>
        </div>

        {regler.isError && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            {(regler.error as any)?.message ?? 'Le renouvellement a échoué : réessayez.'}
          </p>
        )}
      </div>
      )}
    </div>
  );
}
